import { useState } from "react";
import "./NaviiIcon.css";

type NaviiIconProps = {
  size?: number;
  performSearch?: () => Promise<void> | void;
  isSearching?: boolean;
};

export function NaviiIcon({ size = 48, performSearch, isSearching: controlledIsSearching }: NaviiIconProps) {
  const [internalIsSearching, setInternalIsSearching] = useState(false);
  const isSearching = controlledIsSearching ?? internalIsSearching;

  const runSearch = async () => {
    if (!performSearch || isSearching) return;

    setInternalIsSearching(true);

    try {
      await performSearch();
    } finally {
      setTimeout(() => {
        setInternalIsSearching(false);
      }, 900);
    }
  };

  return (
    <div
      className={`navii-search${isSearching ? " searching" : ""}`}
      onClick={runSearch}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.24,
        background:
          "linear-gradient(135deg, #0EA5E9 0%, #1D4ED8 42%, #7C3AED 72%, #D946EF 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        cursor: performSearch ? "pointer" : "default",
        boxShadow:
          "0 10px 24px rgba(79,70,229,.34), inset 0 1px 0 rgba(255,255,255,.26)",
      }}
    >
      {/* Soft shine */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 28% 18%, rgba(255,255,255,.32), transparent 42%)",
          pointerEvents: "none",
        }}
      />

      <svg
        className="navii-icon"
        width={size * 0.76}
        height={size * 0.76}
        viewBox="0 0 64 64"
        fill="none"
        style={{
          position: "relative",
          zIndex: 1,
          filter: "drop-shadow(0 2px 3px rgba(0,0,0,.18))",
        }}
      >
        {/* AI sparkles */}
        <path
          className="sparkle-1"
          d="M8 7L9.5 11L13.5 12.5L9.5 14L8 18L6.5 14L2.5 12.5L6.5 11L8 7Z"
          fill="white"
        />
        <path
          className="sparkle-2"
          d="M56 5L57.2 8.2L60.5 9.5L57.2 10.8L56 14L54.8 10.8L51.5 9.5L54.8 8.2L56 5Z"
          fill="white"
          opacity="0.95"
        />
        <path
          className="sparkle-3"
          d="M8 51L9 53.5L11.5 54.5L9 55.5L8 58L7 55.5L4.5 54.5L7 53.5L8 51Z"
          fill="white"
          opacity="0.9"
        />

        <g className="navii-wheel">
          {/* Ship wheel outer ring */}
          <circle
            cx="32"
            cy="32"
            r="18"
            stroke="white"
            strokeWidth="5"
          />

          {/* Ship wheel inner ring */}
          <circle
            cx="32"
            cy="32"
            r="7"
            stroke="white"
            strokeWidth="5"
          />

          {/* Wheel spokes */}
          <path
            d="M32 11V23"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M32 41V53"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M11 32H23"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M41 32H53"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M17.2 17.2L25.7 25.7"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M38.3 38.3L46.8 46.8"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M46.8 17.2L38.3 25.7"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M25.7 38.3L17.2 46.8"
            stroke="white"
            strokeWidth="4"
            strokeLinecap="round"
          />

          {/* Handle knobs */}
          <circle cx="32" cy="8.5" r="3.5" fill="white" />
          <circle cx="32" cy="55.5" r="3.5" fill="white" />
          <circle cx="8.5" cy="32" r="3.5" fill="white" />
          <circle cx="55.5" cy="32" r="3.5" fill="white" />
          <circle cx="15.4" cy="15.4" r="3.2" fill="white" />
          <circle cx="48.6" cy="48.6" r="3.2" fill="white" />
          <circle cx="48.6" cy="15.4" r="3.2" fill="white" />
          <circle cx="15.4" cy="48.6" r="3.2" fill="white" />

          {/* Center hub */}
          <circle cx="32" cy="32" r="3" fill="white" />
        </g>
      </svg>
    </div>
  );
}
